import React, { useEffect, useState } from "react";
import axios from "axios";
import { SMS_TYPE_LABEL, SMS_STATUS_LABEL, label } from "@/lib/labels";
import { useApp, API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { MessageSquare, Phone } from "lucide-react";

export default function SMSLog() {
  const { t } = useApp();
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [typeFilter, setTypeFilter] = useState("all");

  const load = async () => {
    const params = { page, page_size: pageSize };
    if (typeFilter !== "all") params.sms_type = typeFilter;
    const r = await axios.get(`${API}/sms-logs`, { params });
    const data = r.data;
    if (Array.isArray(data)) {
      setLogs(data); setTotal(data.length); setPages(1);
    } else {
      setLogs(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, typeFilter]);

  const fmt = (d) => new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  const typeColor = (tp) => {
    if (!tp) return "#5C6B61";
    if (tp.startsWith("reminder")) return "#D96C4A";
    return ({
      welcome: "#2E7D32",
      reminder: "#D96C4A",
      confirmation: "#1E4631",
      campaign: "#E3A72F",
      ad: "#5C6B61",
      advice: "#1E4631",
    }[tp] || "#5C6B61");
  };

  const types = ["all", "welcome", "confirmation", "reminder", "reminder-j-15", "reminder-j-3", "reminder-45min", "campaign", "ad", "advice"];

  return (
    <div className="space-y-6" data-testid="sms-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-[Outfit] font-bold">{t.dash.sms}</h1>
        <span className="text-sm text-[#5C6B61]">{total} message(s)</span>
      </div>

      <div className="bg-[#E3A72F]/10 border border-[#E3A72F]/30 rounded-xl px-5 py-3 text-sm text-[#1A1D1A]">
        <strong>MODE DÉMO :</strong> Les SMS sont simulés (MOCK). En production, intégration Twilio ou opérateur RDC (Vodacom, Orange, Airtel).
      </div>

      <div className="flex flex-wrap gap-2 bg-white p-3 rounded-xl border border-[#E5EBE7]" data-testid="sms-filters">
        {types.map((tp) => (
          <button key={tp} onClick={() => { setPage(1); setTypeFilter(tp); }}
            className={`px-3 py-1 rounded-full text-xs font-medium transition ${
              typeFilter === tp ? "bg-[#1E4631] text-white" : "bg-[#FDFBF7] text-[#5C6B61] hover:text-[#1E4631]"
            }`}
            data-testid={`sms-filter-${tp}`}>
            {tp === "all" ? "Tous" : label(SMS_TYPE_LABEL, tp)}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-[#E5EBE7] divide-y divide-[#E5EBE7]">
        {logs.length === 0 ? (
          <div className="p-12 text-center text-[#5C6B61]" data-testid="empty-sms">Aucun SMS envoyé</div>
        ) : logs.map((l) => (
          <div key={l.id} className="p-5 hover:bg-[#FDFBF7]/50 transition" data-testid={`sms-${l.id}`}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full grid place-items-center" style={{ background: `${typeColor(l.type)}15`, color: typeColor(l.type) }}>
                <MessageSquare className="w-4 h-4"/>
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <span className="inline-flex items-center gap-1 font-medium text-[#1A1D1A]">
                    <Phone className="w-3 h-3"/> {l.phone}
                  </span>
                  <span className="px-2 py-0.5 rounded-full" style={{ background: `${typeColor(l.type)}15`, color: typeColor(l.type) }}>{label(SMS_TYPE_LABEL, l.type)}</span>
                  <span className="text-[#5C6B61]">{fmt(l.sent_at)}</span>
                  <span className="text-[#2E7D32]">· {label(SMS_STATUS_LABEL, l.status)}</span>
                </div>
                <p className="mt-2 text-sm text-[#1A1D1A] leading-relaxed">{l.message}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between bg-white p-3 rounded-xl border border-[#E5EBE7]" data-testid="sms-pagination">
          <div className="text-xs text-[#5C6B61]">Page {page} / {pages} · {total} message(s)</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={()=>setPage(p=>Math.max(1,p-1))} data-testid="sms-page-prev">Précédent</Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={()=>setPage(p=>Math.min(pages,p+1))} data-testid="sms-page-next">Suivant</Button>
          </div>
        </div>
      )}
    </div>
  );
}
